import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { installationCreateGoodsFamilyValues } from '@/features/installations/components/installation-create/constants'
import type { InstallationGoodsFamily } from '@/features/installations/installation-create.schema'
import type { SupplierInstallationFormState } from '@/features/suppliers/lib/supplier-installation-form-state'
type SupplierInstallationGeneralSectionCardProps = {
  state: SupplierInstallationFormState
  doesRequireLocalName: boolean
  countryCode: string
  nameError?: string
  variant?: 'card' | 'plain'
  onChange: (evt: React.ChangeEvent<HTMLInputElement>) => void
  onGoodsFamilyChange: (value: InstallationGoodsFamily | '') => void
}

export function SupplierInstallationGeneralSectionCard({
  state,
  doesRequireLocalName,
  countryCode,
  nameError,
  variant = 'card',
  onChange,
  onGoodsFamilyChange,
}: SupplierInstallationGeneralSectionCardProps) {
  const { t } = useTranslation('installations')

  const fields = (
    <>
        {doesRequireLocalName ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="localName">
                <span className="inline-flex items-center gap-1">
                  {t('create.fields.localName.label')}
                  <ReactCountryFlag countryCode={countryCode || 'cn'} svg />
                </span>
              </Label>
              <Input id="localName" value={state.localName} onChange={onChange} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">
                <span className="inline-flex items-center gap-1">
                  {t('create.fields.name.label')}
                  <ReactCountryFlag countryCode="gb" svg />
                </span>
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="name"
                value={state.name}
                onChange={onChange}
                aria-required
                aria-invalid={Boolean(nameError)}
              />
              {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
            </div>
          </div>
        ) : (
          <div className="grid gap-2">
            <Label htmlFor="name">
              {t('create.fields.name.label')}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="name"
              value={state.name}
              onChange={onChange}
              aria-required
              aria-invalid={Boolean(nameError)}
            />
            {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
          </div>
        )}
        <div className="grid gap-2">
          <Label htmlFor="goodsFamily">{t('create.fields.goodsFamily.label')}</Label>
          <Select
            value={state.goodsFamily || undefined}
            onValueChange={(value) =>
              onGoodsFamilyChange(value as InstallationGoodsFamily)
            }
          >
            <SelectTrigger id="goodsFamily" className="w-full">
              <SelectValue placeholder={t('create.fields.goodsFamily.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {installationCreateGoodsFamilyValues.map((goodsFamily) => (
                <SelectItem key={goodsFamily} value={goodsFamily}>
                  {t(`create.fields.goodsFamily.options.${goodsFamily}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
    </>
  )

  if (variant === 'plain') {
    return <div className="space-y-4">{fields}</div>
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Info className="size-5 shrink-0" />
          <CardTitle>
            {t('suppliers.edit.installations.edit.general.title')}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{fields}</CardContent>
    </Card>
  )
}
